<div class="container py-3">
  <h2 class="mb-3">{{ title }}</h2>

  <div class="alert alert-info">
    <p class="mb-1">
      PPK Downloader (Pro Edition) lets you schedule RTCM recordings directly from any external NTRIP mount point you have configured in <a class="link-primary" routerLink="/ext-mountpoint-connections">External MountPoint Connections</a>.
    </p>
    <p class="mb-0">
      Pick a mount point, set the recording window, and submit the recording task. Once the file is ready you can download it below.
    </p>
  </div>

  <div class="card shadow-sm">
    <div class="card-body">
      <div class="row g-3 align-items-end mb-3">
        <div class="col-md-6">
          <label class="form-label small">Search (mount point, downloader user, server)</label>
          <div class="input-group input-group-sm">
            <input type="text" class="form-control" [(ngModel)]="search" placeholder="Search keyword">
            <button class="btn btn-outline-primary" type="button" (click)="onSearch()">Search</button>
          </div>
        </div>
        <div class="col-md-6 text-md-end">
          <div *ngIf="isLoading" class="text-muted small">
            Loading connections...
          </div>
          <div *ngIf="!isLoading && !hasConnections" class="text-muted small">
            No mount point connections found.
          </div>
        </div>
      </div>

      <div *ngIf="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>

      <ng-container *ngIf="hasConnections">
        <div class="d-flex justify-content-between align-items-center mt-2 mb-3">
          <div><strong>Mount Point:</strong> {{ currentPage }} / {{ totalPages }}</div>
          <div class="btn-toolbar" role="toolbar" aria-label="pagination">
            <div class="btn-group me-2" role="group">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goFirst()" [disabled]="currentPage === 1">« First</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goPrev()" [disabled]="currentPage === 1">‹ Prev</button>
            </div>
            <div class="btn-group me-2" role="group">
              <button *ngFor="let p of pageWindow" type="button"
                      class="btn btn-sm"
                      [ngClass]="{'btn-primary': p === currentPage, 'btn-outline-secondary': p !== currentPage}"
                      (click)="goToPage(p)">{{ p }}</button>
            </div>
            <div class="btn-group" role="group">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goNext()" [disabled]="currentPage === totalPages">Next ›</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goLast()" [disabled]="currentPage === totalPages">Last »</button>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end align-items-center mb-3">
          <label class="me-2 mb-0 small">Select a Mount Point:</label>
          <div class="mp-select-wrapper" style="min-width: 280px;">
            <select class="form-select form-select-sm"
                    [ngModel]="getConnectionKey(currentConnection)"
                    (ngModelChange)="selectFromDropdown($event)">
              <option value=""></option>
              <option *ngFor="let c of sortedConnections" [value]="getConnectionKey(c)">
                {{ c.mountPoint }}
              </option>
            </select>
          </div>
        </div>

        <div class="table-responsive pro-table mb-4">
          <table class="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Mount Point</th>
                <th scope="col">Server</th>
                <th scope="col">Port</th>
                <th scope="col">Downloader User</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let c of sortedConnections; let i = index"
                  (click)="goToConnection(c)"
                  [class.table-active]="getConnectionKey(c) === getConnectionKey(selectedConnection)">
                <td>{{ i + 1 }}</td>
                <td>{{ c.mountPoint }}</td>
                <td>{{ c.server }}</td>
                <td>{{ c.port }}</td>
                <td>{{ c.downloaderUserName }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div *ngIf="selectedConnection as conn" class="mp-card">
          <div class="row g-3">
            <div class="col-12 col-lg-7">
              <div class="alert alert-light border mb-3">
                <div class="row g-2">
                  <div class="col-sm-6">
                    <div class="label">Mount Point</div>
                    <div class="value">{{ conn.mountPoint }}</div>
                  </div>
                  <div class="col-sm-6">
                    <div class="label">Server</div>
                    <div class="value">{{ conn.server }}:{{ conn.port }}</div>
                  </div>
                  <div class="col-sm-6">
                    <div class="label">Downloader User</div>
                    <div class="value">{{ conn.downloaderUserName }}</div>
                  </div>
                  <div class="col-sm-6">
                    <div class="label">Downloader Password</div>
                    <div class="value">{{ conn.downloaderPassword }}</div>
                  </div>
                </div>
              </div>

              <div class="subsection">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h4 class="subsection-title m-0">Recording Task</h4>
                </div>
                <div class="row g-2">
                  <div class="col-md-5">
                    <label class="form-label small">Recording Start Time</label>
                    <input type="datetime-local" class="form-control form-control-sm"
                           [ngModel]="getPlanStartAtInput(conn)"
                           (ngModelChange)="onPlanStartAtInputChange(conn, $event)"
                           (keydown)="$event.preventDefault()" (keypress)="$event.preventDefault()" (paste)="$event.preventDefault()">
                  </div>
                  <div class="col-md-4">
                    <label class="form-label small">Recording Duration</label>
                    <select class="form-select form-select-sm"
                            [ngModel]="getDurationSelection(conn)"
                            (ngModelChange)="onDurationSelectionChange(conn, $event)">
                      <option *ngFor="let opt of durationPresetOptions" [ngValue]="opt.minutes">{{ opt.label }}</option>
                    </select>
                  </div>
                </div>
                <div class="d-flex align-items-center mt-3">
                  <button class="btn btn-sm btn-primary"
                          (click)="onSubmitRecording(conn)"
                          [disabled]="isSubmitting(conn)">
                    Submit the Recording Task
                  </button>
                  <button class="btn btn-sm btn-outline-secondary ms-2" (click)="onRefreshStatus(conn)">
                    Refresh status
                  </button>
                </div>
              </div>

              <div class="record-card mt-3">
                <div class="fw-semibold mb-1">Recorded RTK Base Data File for Download:</div>
                <div class="file-card">
                  <ng-container *ngIf="downloaderUrlByMountpoint[conn.mountPoint] as dl; else dlEmpty">
                    <ng-container *ngIf="!dl?.running; else runningTpl">
                      <ng-container *ngIf="dl?.url; else noFileTpl">
                        <div class="d-flex align-items-center">
                          <span class="material-icons me-2">insert_drive_file</span>
                          <div>
                            <div class="fw-semibold">rtcm_data.rtcm3</div>
                            <div class="text-muted small">
                              Type: RTCM3 · Created: {{ dl.lastModified ? (dl.lastModified | date:'medium') : '--' }}
                            </div>
                            <div class="text-primary small" *ngIf="!isExpired(dl); else expiredTpl">
                              (This download link will expire in {{ getExpiryDate(dl) | date:'medium' }}. Please download before it expires.)
                            </div>
                            <ng-template #expiredTpl>
                              <div class="text-primary small">(This file has expired and is no longer available for download.)</div>
                            </ng-template>
                          </div>
                        </div>
                        <div>
                          <a class="btn btn-sm btn-outline-primary"
                             [href]="isExpired(dl) ? null : dl.url"
                             [class.disabled]="isExpired(dl)"
                             [attr.aria-disabled]="isExpired(dl) ? true : null"
                             (click)="isExpired(dl) ? $event.preventDefault() : null"
                             target="_blank" rel="noopener" download>
                            Download
                          </a>
                        </div>
                      </ng-container>
                      <ng-template #noFileTpl>
                        <div class="text-muted">No recorded file available for download</div>
                      </ng-template>
                    </ng-container>
                    <ng-template #runningTpl>
                      <div class="text-primary small">A recorder is currently running or scheduled. Please check back later.</div>
                    </ng-template>
                  </ng-container>
                  <ng-template #dlEmpty>
                    <div class="text-muted">No recorded file available for download</div>
                  </ng-template>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-5 d-flex align-items-start">
              <img src="assets/images/landing/GeoAstra_PPK_Downloader_diagram.drawio.png"
                   alt="PPK Downloader Diagram"
                   class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  </div>
</div>


